import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { apiClient } from '../../../services/api';
import { useToast } from '../../../contexts/ToastContext';
import { NotificationProvider } from '../../../types/domain';

export function useNotificationProviders() {
  const { currentProject, currentOrg, canAdmin } = useAuth();
  const { showToast } = useToast();

  const [providers, setProviders] = useState<NotificationProvider[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [providerModalOpen, setProviderModalOpen] = useState(false);

  // New Provider Form state
  const [providerType, setProviderType] = useState<'smtp' | 'resend'>('smtp');
  const [providerName, setProviderName] = useState('');
  // SMTP fields
  const [smtpHost, setSmtpHost] = useState('smtp.mailpit.local');
  const [smtpPort, setSmtpPort] = useState('1025');
  const [smtpMode, setSmtpMode] = useState<'none' | 'starttls' | 'tls'>('none');
  const [smtpUsername, setSmtpUsername] = useState('');
  const [smtpPassword, setSmtpPassword] = useState('');
  const [smtpFrom, setSmtpFrom] = useState('notifications@ericius.app');
  const [smtpFromName, setSmtpFromName] = useState('Ericius Notifications');
  const [showSmtpPassword, setShowSmtpPassword] = useState(false);
  const [isTestingSmtp, setIsTestingSmtp] = useState(false);
  const [smtpTestResult, setSmtpTestResult] = useState<{
    success: boolean;
    message?: string;
    error?: string;
  } | null>(null);

  // Resend fields
  const [resendApiKey, setResendApiKey] = useState('');
  const [resendFrom, setResendFrom] = useState('notifications@ericius.app');
  const [isSavingProvider, setIsSavingProvider] = useState(false);

  // Deletion state
  const [providerToDelete, setProviderToDelete] = useState<NotificationProvider | null>(null);
  const [isDeletingProvider, setIsDeletingProvider] = useState(false);

  // Per-card testing state
  const [testingCardId, setTestingCardId] = useState<string | null>(null);
  const [cardTestStatus, setCardTestStatus] = useState<Record<string, { success: boolean; message: string }>>({});

  const { projectId: routeProjectId } = useParams<{ projectId?: string }>();
  const projectId = routeProjectId || currentProject?.id;

  const fetchProviders = useCallback(async (isSilent = false) => {
    if (!projectId) {
      setIsLoading(false);
      return;
    }
    if (!isSilent) setIsLoading(true);
    try {
      const res = await apiClient<{ data: NotificationProvider[] }>(
        `/projects/${projectId}/providers`
      );
      setProviders(res.data || []);
    } catch (err) {
      console.warn('Could not load providers:', err);
      setProviders([]);
    } finally {
      setIsLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    fetchProviders();
  }, [fetchProviders]);

  const lastFocusRef = useRef<number>(0);
  useEffect(() => {
    const handleFocus = () => {
      const now = Date.now();
      if (now - lastFocusRef.current < 4000) return;
      lastFocusRef.current = now;
      fetchProviders(true);
    };
    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [fetchProviders]);

  const handleTestSmtp = async () => {
    if (!currentProject) return;
    setIsTestingSmtp(true);
    setSmtpTestResult(null);

    try {
      const res = await apiClient<{ success: boolean; message?: string; error?: string }>(
        `/projects/${currentProject.id}/providers/test-smtp`,
        {
          method: 'POST',
          body: JSON.stringify({
            host: smtpHost.trim(),
            port: parseInt(smtpPort, 10),
            mode: smtpMode,
            username: smtpUsername.trim(),
            password: smtpPassword,
            from_address: smtpFrom.trim(),
            from_name: smtpFromName.trim(),
          }),
        }
      );

      if (res.success) {
        setSmtpTestResult({
          success: true,
          message: res.message || 'SMTP handshake and authentication verified successfully (250 OK)',
        });
      } else {
        setSmtpTestResult({
          success: false,
          error: res.error || 'Connection verification failed',
        });
      }
    } catch (err: any) {
      setSmtpTestResult({
        success: false,
        error:
          err.data?.error ||
          err.data?.message ||
          err.message ||
          'Connection refused or timed out after 5000ms',
      });
    } finally {
      setIsTestingSmtp(false);
    }
  };

  const handleTestExistingProvider = async (provider: NotificationProvider) => {
    if (!currentProject || provider.provider_type !== 'smtp') return;
    setTestingCardId(provider.id);

    try {
      const res = await apiClient<{ success: boolean; message?: string; error?: string }>(
        `/projects/${currentProject.id}/providers/test-smtp`,
        {
          method: 'POST',
          body: JSON.stringify({
            host: provider.config.host,
            port: provider.config.port,
            mode: provider.config.mode || 'none',
            username: provider.config.username || '',
            password: provider.config.password || '',
            from_address: provider.config.from_address || '',
            from_name: provider.config.from_name || '',
          }),
        }
      );

      if (res.success) {
        setCardTestStatus((prev) => ({
          ...prev,
          [provider.id]: { success: true, message: res.message || 'Verified (250 OK)' },
        }));
        showToast({
          type: 'success',
          title: 'SMTP Connection Verified',
          message: `Handshake to ${provider.config.host}:${provider.config.port} succeeded.`,
        });
      } else {
        setCardTestStatus((prev) => ({
          ...prev,
          [provider.id]: { success: false, message: res.error || 'Verification failed' },
        }));
        showToast({
          type: 'error',
          title: 'SMTP Test Failed',
          message: res.error || 'Handshake failed',
        });
      }
    } catch (err: any) {
      setCardTestStatus((prev) => ({
        ...prev,
        [provider.id]: { success: false, message: err?.message || 'Connection timed out' },
      }));
      showToast({
        type: 'error',
        title: 'Connection Error',
        message: err?.message || 'Could not reach SMTP server',
      });
    } finally {
      setTestingCardId(null);
    }
  };

  const handleSaveProvider = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentProject || !canAdmin) return;
    setIsSavingProvider(true);

    const providerConfig =
      providerType === 'smtp'
        ? {
            host: smtpHost.trim(),
            port: parseInt(smtpPort, 10),
            mode: smtpMode,
            username: smtpUsername.trim(),
            password: smtpPassword,
            from_address: smtpFrom.trim(),
            from_name: smtpFromName.trim(),
          }
        : { api_key: resendApiKey.trim(), from_address: resendFrom.trim() };

    const newProvider: NotificationProvider = {
      id: Math.random().toString(36).substring(2, 9),
      project_id: currentProject.id,
      organization_id: currentOrg?.id || '',
      name: providerName.trim() || `${providerType.toUpperCase()} Connector`,
      provider_type: providerType,
      config: providerConfig,
      is_default: providers.length === 0,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    };

    try {
      const res = await apiClient<{ provider: NotificationProvider }>(
        `/projects/${currentProject.id}/providers`,
        {
          method: 'POST',
          body: JSON.stringify(newProvider),
        }
      );
      if (res.provider) {
        setProviders((prev) => [...prev, res.provider]);
      } else {
        setProviders((prev) => [...prev, newProvider]);
      }

      showToast({
        type: 'success',
        title: 'Provider Configured',
        message: `${newProvider.name} is now available for datastore notification rules.`,
      });

      setProviderModalOpen(false);
      setProviderName('');
      setSmtpTestResult(null);
    } catch (err: any) {
      showToast({
        type: 'error',
        title: 'Failed to configure provider',
        message: err?.message || 'Server error saving provider connector',
      });
    } finally {
      setIsSavingProvider(false);
    }
  };

  const handleDeleteProvider = (provider: NotificationProvider) => {
    if (!canAdmin) return;
    setProviderToDelete(provider);
  };

  const executeDeleteProvider = async () => {
    if (!currentProject || !canAdmin || !providerToDelete) return;
    setIsDeletingProvider(true);
    try {
      await apiClient(`/projects/${currentProject.id}/providers/${providerToDelete.id}`, {
        method: 'DELETE',
      });
      setProviders((prev) => prev.filter((pr) => pr.id !== providerToDelete.id));
      showToast({
        type: 'success',
        title: 'Provider Removed',
        message: 'Bring-Your-Own Provider connector deleted.',
      });
      setProviderToDelete(null);
    } catch (err: any) {
      showToast({
        type: 'error',
        title: 'Failed to delete provider',
        message: err?.message || 'Server error',
      });
    } finally {
      setIsDeletingProvider(false);
    }
  };

  const openAddProviderModal = () => {
    setSmtpTestResult(null);
    setProviderModalOpen(true);
  };

  return {
    providers,
    isLoading,
    providerModalOpen,
    setProviderModalOpen,
    openAddProviderModal,
    providerType,
    setProviderType,
    providerName,
    setProviderName,
    smtpHost,
    setSmtpHost,
    smtpPort,
    setSmtpPort,
    smtpMode,
    setSmtpMode,
    smtpUsername,
    setSmtpUsername,
    smtpPassword,
    setSmtpPassword,
    smtpFrom,
    setSmtpFrom,
    smtpFromName,
    setSmtpFromName,
    showSmtpPassword,
    setShowSmtpPassword,
    isTestingSmtp,
    smtpTestResult,
    resendApiKey,
    setResendApiKey,
    resendFrom,
    setResendFrom,
    isSavingProvider,
    providerToDelete,
    setProviderToDelete,
    isDeletingProvider,
    testingCardId,
    cardTestStatus,
    handleTestSmtp,
    handleTestExistingProvider,
    handleSaveProvider,
    handleDeleteProvider,
    executeDeleteProvider,
    canAdmin,
  };
}
